import React from 'react'
import {
    doesMeetRequirementsToBeAnEmail,
    isTheSameEmail,
} from '../../helpers/email-validation-helper'
import { IMyAccountContext } from '../../hooks/useMyAccountContext'
import { ChangeEmailFormProps } from '../ChangeEmail'
import {
    BannerHelperProps,
    getAndFireAppropriateBannerHelper,
} from './change-email-banner-helper'

interface EmailChangeMutateProps {
    sendForgotPasswordEmail: () => any
    sendEmailChangeUpdate: () => any
    sendVerifyEmail: () => any
}
// Props to send through the submitting values
interface EmailChangeSubmittingStateProps {
    submitting: boolean
    setSubmitting: React.Dispatch<React.SetStateAction<boolean>>
}
// Props to send through the change email values
interface EmailChangeStateProps {
    emailChangeProps: ChangeEmailFormProps
    setEmailChangeProps: React.Dispatch<
        React.SetStateAction<ChangeEmailFormProps>
    >
}
// Props used to send through to all click helpers
export interface ClickHelperProps {
    // Context values below:
    myAccountContext: IMyAccountContext
    mutateProps: EmailChangeMutateProps
    // State values below:
    emailChangeState: EmailChangeStateProps
    submittingState: EmailChangeSubmittingStateProps
}

// Handle whenever someone clicks on the 'forgot password' button/links.
export const onForgotPasswordClick = async (
    props: ClickHelperProps,
    bannerProps: BannerHelperProps,
) => {
    const { myAccountContext, mutateProps } = props
    const { sendForgotPasswordEmail } = mutateProps
    const { setMessageBanner } = myAccountContext

    setMessageBanner(undefined)
    const res = await sendForgotPasswordEmail()

    getAndFireAppropriateBannerHelper(
        res.result ? 'forgot-password-success' : 'forgot-password-error',
        bannerProps,
        res.message,
    )
}

// Handle whenever someone clicks on 'SAVE' while trying to change an email
export const onChangeEmailFormSubmitHandler = async (
    props: ClickHelperProps,
    bannerProps: BannerHelperProps,
    e:
        | React.MouseEvent<HTMLElement, globalThis.MouseEvent>
        | React.KeyboardEvent<HTMLElement>,
) => {
    const { myAccountContext, emailChangeState, submittingState, mutateProps } =
        props
    const { errorState } = bannerProps
    const { emailChangeProps } = emailChangeState
    const { email, currentPassword, newEmail } = emailChangeProps
    const { setSubmitting } = submittingState
    const { setMessageBanner } = myAccountContext
    const { setErrors } = errorState
    const { sendEmailChangeUpdate, sendVerifyEmail } = mutateProps

    e.preventDefault()

    // Check for the simple stuff first
    if (!doesMeetRequirementsToBeAnEmail(newEmail)) {
        getAndFireAppropriateBannerHelper('invalid-email', bannerProps)
        return
    }
    if (isTheSameEmail(newEmail, email)) {
        getAndFireAppropriateBannerHelper('same-email', bannerProps)
        return
    }
    if (currentPassword.length === 0) {
        getAndFireAppropriateBannerHelper('incorrect-password', bannerProps)
        return
    }

    setMessageBanner(undefined)
    setErrors({
        emailError: false,
        passwordError: false,
    })

    setSubmitting(true)
    const res = await sendEmailChangeUpdate()
    setSubmitting(false)

    // Fire the appropriate banner
    getAndFireAppropriateBannerHelper(res.bannerType, bannerProps)

    if (res.result) {
        sendVerifyEmail()
    }
}

// The user wishes to cancel the email change, so clear everything and go back
// to the parent page.
export const onChangeEmailFormCancelHelper = (
    props: ClickHelperProps,
    e: React.MouseEvent<HTMLElement, globalThis.MouseEvent>,
) => {
    const { myAccountContext } = props
    const { setMessageBanner, goBackToParent } = myAccountContext

    e.preventDefault()
    setMessageBanner(undefined)
    goBackToParent(true)
}
